// ============================================================================
// Component-scoped saved-view render-read owner
// ============================================================================
//
// This is the one owner for automatic app_saved_views reads.  A saved-view
// picker can appear in many hubs, but its cache identity is always the active
// authenticated subject scope plus the picker surface key.  Route catalog
// resources intentionally remain untouched: this sidecar is a shared control,
// not a declaration that a route owns its entire page.
// ============================================================================

import {skipToken, useQuery} from '@tanstack/react-query';
import {useCallback, useMemo, useRef} from 'react';

import {useAuth} from '../contexts/AuthContext.js';
import {listSavedViews} from '../lib/savedViewsApi.js';
import {sb as defaultClient} from '../lib/supabase.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import {resourceKey, scopeOrNull, UNRESOLVED_RESOURCE_KEY} from '../app/data/routeDataCatalog.js';
import type {AuthStateInput, QueryScope} from '../app/data/routeDataCatalog.js';
import {SAVED_VIEWS_RESOURCE} from './savedViewsRegistry.js';

export {SAVED_VIEWS_RESOURCE};

export type SavedViewsRefetchResult = {data?: unknown[]; error?: unknown};

export type SavedViewsData = {
  /** Empty on a failed read, matching every former picker-local effect. */
  data: unknown[];
  /** Includes deliberate same-key refetches, matching the old disabled picker timing. */
  loading: boolean;
  error: unknown;
  /** No-op until a resolved authenticated scope and a surface key both exist. */
  refetch: () => Promise<SavedViewsRefetchResult | undefined>;
};

export type UseSavedViewsDataOptions = {
  client?: SupabaseClient;
  enabled?: boolean;
};

const EMPTY_SAVED_VIEWS: unknown[] = [];

export function savedViewsErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as {message?: unknown}).message;
    if (typeof message === 'string' && message) return message;
  }
  return String(error);
}

function authenticatedSavedViewsScope(authState: unknown, rolePreview: unknown): QueryScope | null {
  // app_saved_views is an authenticated RLS resource.  Do not allow an
  // accidental signed-out/public mount to create a public cache namespace.
  if (!authState || typeof authState !== 'object') return null;
  const scope = scopeOrNull(authState as AuthStateInput, rolePreview);
  return scope && scope.kind === 'authenticated' ? scope : null;
}

function savedViewsQueryKey(scope: QueryScope | null, surfaceKey: string): readonly unknown[] {
  return scope && surfaceKey ? resourceKey(SAVED_VIEWS_RESOURCE.LIST, scope, {surfaceKey}) : UNRESOLVED_RESOURCE_KEY;
}

/**
 * Subscribe to one saved-view picker.  Query performs request de-duplication,
 * keeps result data in its subject-scoped key, and fences stale scopes without
 * moving any route resource or creating a boot claim.
 */
export function useSavedViewsData(
  requestedSurfaceKey: string | null | undefined,
  {client = defaultClient, enabled = true}: UseSavedViewsDataOptions = {},
): SavedViewsData {
  const {authState, rolePreview} = useAuth();
  const surfaceKey = typeof requestedSurfaceKey === 'string' ? requestedSurfaceKey.trim() : '';
  const scope = useMemo(() => authenticatedSavedViewsScope(authState, rolePreview), [authState, rolePreview]);
  const ready = Boolean(enabled && scope && surfaceKey);

  const query = useQuery<unknown[]>({
    queryKey: savedViewsQueryKey(scope, surfaceKey),
    // Destructuring signal is deliberate: Query only aborts an orphaned
    // request when the query function observes its AbortSignal.
    queryFn: ready ? ({signal}) => listSavedViews(client, surfaceKey, signal) : skipToken,
    enabled: ready,
  });

  // Components hold this callback in mutation handlers and retry controls.
  // Keep that public identity stable while refusing Query's otherwise allowed
  // refetch of a disabled/unresolved observer.
  const readyRef = useRef(ready);
  readyRef.current = ready;
  const refetchRef = useRef(query.refetch);
  refetchRef.current = query.refetch;
  const refetch = useCallback(async () => {
    if (!readyRef.current) return Promise.resolve(undefined);
    try {
      return (await refetchRef.current()) as SavedViewsRefetchResult;
    } catch (error) {
      // Existing picker-local loaders caught their own refresh failure, so a
      // successful create/update/delete never became a failed mutation merely
      // because its follow-up read failed.  Query still owns the visible error.
      return {error};
    }
  }, []);

  const error = ready ? (query.error ?? null) : null;
  return {
    // Query correctly retains last-good data after a refetch failure.  These
    // pickers historically clear instead, so preserve that independent UI
    // contract without reviving seventeen independent reads.
    data: error || !Array.isArray(query.data) ? EMPTY_SAVED_VIEWS : query.data,
    loading: Boolean(enabled && (!scope || query.isPending || query.isFetching)),
    error,
    refetch,
  };
}
